import { useState } from 'react'
import { supabase } from './supabaseClient'

const inp = {
  width: '100%',
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 8,
  padding: '11px 14px',
  color: '#f1f5f9',
  fontSize: 14,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
  marginBottom: 12,
}

const btnStyle = (bg) => ({
  width: '100%',
  background: bg,
  border: 'none',
  borderRadius: 8,
  padding: '12px',
  color: '#fff',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginBottom: 8,
})

export default function Auth() {
  const [mode, setMode] = useState('login') // login | signup | reset
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  async function handleLogin() {
    setLoading(true); setError(''); setMessage('')
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }

  async function handleSignup() {
    setLoading(true); setError(''); setMessage('')
    const { error } = await supabase.auth.signUp({ email, password })
    if (error) setError(error.message)
    else setMessage('Check your email to confirm your account.')
    setLoading(false)
  }

  async function handleReset() {
    setLoading(true); setError(''); setMessage('')
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    if (error) setError(error.message)
    else setMessage('Password reset email sent. Check your inbox.')
    setLoading(false)
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#020617',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'IBM Plex Mono', monospace",
      padding: 20,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: '#0f172a',
        border: '1px solid #1e293b',
        borderRadius: 16,
        padding: 32,
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.03em' }}>
            ali<span style={{ color: '#f59e0b' }}>can</span>b
          </div>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>All I Can Be</div>
        </div>

        {/* Tagline */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.4, marginBottom: 8 }}>
            Close the gap between who you are<br/>and who you can be.
          </div>
          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, fontStyle: 'italic' }}>
            Track your goals, habits, time and mood daily — and let AI reveal the patterns that turn your potential into reality.
          </div>
        </div>

        {/* YouTube link - update URL when channel is ready */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <a href="https://youtube.com/@alicanb" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: '#1e293b', border: '1px solid #334155',
            borderRadius: 20, padding: '6px 14px',
            color: '#64748b', fontSize: 12, textDecoration: 'none',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor='#ef4444'; e.currentTarget.style.color='#ef4444'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor='#334155'; e.currentTarget.style.color='#64748b'; }}>
            <span style={{ fontSize: 16 }}>▶</span> Learn, grow and get inspired on YouTube
          </a>
        </div>

        {/* Science bullets */}
        <div style={{
          background: '#020617', border: '1px solid #1e293b',
          borderRadius: 12, padding: '16px 18px', marginBottom: 24,
        }}>
          <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 12 }}>
            WHY IT WORKS
          </div>
          {[
            { icon: '🎯', text: 'Written goals are achieved 2–3× more often', source: 'Locke, Goal Setting Theory' },
            { icon: '⏱', text: 'Tracking time changes how you spend it', source: 'Drucker — "what gets measured gets managed"' },
            { icon: '🔄', text: 'Daily reflection builds lasting habits', source: 'CBT & Behavioral Psychology' },
            { icon: '😊', text: 'Mood + activity correlation reveals hidden patterns', source: 'Clinical Psychology' },
            { icon: '🌙', text: 'Planning tomorrow tonight doubles follow-through', source: 'Gollwitzer, Implementation Intentions' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: i < 4 ? 10 : 0, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
              <div>
                <div style={{ fontSize: 12, color: '#cbd5e1', lineHeight: 1.4 }}>{item.text}</div>
                <div style={{ fontSize: 10, color: '#334155', marginTop: 2, fontStyle: 'italic' }}>{item.source}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: 4, background: '#020617', borderRadius: 10, padding: 4, marginBottom: 24 }}>
          {['login', 'signup'].map(m => (
            <button key={m} onClick={() => { setMode(m); setError(''); setMessage('') }} style={{
              flex: 1, padding: '7px', borderRadius: 7, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.15s',
              background: mode === m ? '#1e293b' : 'transparent',
              color: mode === m ? '#f1f5f9' : '#475569',
            }}>{m === 'login' ? 'Sign In' : 'Sign Up'}</button>
          ))}
        </div>

        {/* Form */}
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          placeholder="Email address"
          style={inp}
          onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleSignup())}
        />

        {mode !== 'reset' && (
          <div style={{ position: 'relative', marginBottom: 12 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              style={{ ...inp, marginBottom: 0, paddingRight: 40 }}
              onKeyDown={e => e.key === 'Enter' && (mode === 'login' ? handleLogin() : handleSignup())}
            />
            <button onClick={() => setShowPassword(s => !s)} style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', color: '#475569',
              fontSize: 16, padding: 0, lineHeight: 1,
            }} type="button" title={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? '🙈' : '👁'}
            </button>
          </div>
        )}

        {error && (
          <div style={{ background: '#ef444422', border: '1px solid #ef444444', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#ef4444' }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{ background: '#10b98122', border: '1px solid #10b98144', borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#10b981' }}>
            {message}
          </div>
        )}

        {mode === 'login' && (
          <>
            <button onClick={handleLogin} disabled={loading} style={btnStyle('#f59e0b')}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
            <button onClick={() => { setMode('reset'); setError(''); setMessage('') }}
              style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}>
              Forgot password?
            </button>
          </>
        )}

        {mode === 'signup' && (
          <button onClick={handleSignup} disabled={loading} style={btnStyle('#10b981')}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        )}

        {mode === 'reset' && (
          <>
            <button onClick={handleReset} disabled={loading} style={btnStyle('#8b5cf6')}>
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
            <button onClick={() => { setMode('login'); setError(''); setMessage('') }}
              style={{ background: 'none', border: 'none', color: '#475569', fontSize: 12, cursor: 'pointer', width: '100%', fontFamily: 'inherit' }}>
              Back to sign in
            </button>
          </>
        )}

        {/* Privacy note */}
        <div style={{ fontSize: 11, color: '#334155', textAlign: 'center', marginTop: 20, lineHeight: 1.5 }}>
          Your data is private, encrypted, and never shared.<br />
          alicanb — All I Can Be 🌱
        </div>

        {/* Powered by Claude */}
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 11, color: '#1e293b' }}>
            AI powered by{' '}
            <a href="https://anthropic.com" target="_blank" rel="noopener noreferrer"
              style={{ color: '#334155', textDecoration: 'none', fontWeight: 600 }}
              onMouseEnter={e => e.target.style.color='#f59e0b'}
              onMouseLeave={e => e.target.style.color='#334155'}>
              Claude — Anthropic
            </a>
          </span>
        </div>
      </div>
    </div>
  )
}
